import { SaveSalesGoalsRequest, SalesGoalInput } from './services/requests/save-sales-goals.request';

export interface GoalMonth {
    year: number;
    month: number;
}

export type ProgressLevel = 'done' | 'onTrack' | 'behind';

export function goalMonthOf(isoDay: string): GoalMonth {
    const [year, month] = isoDay.slice(0, 7).split('-').map(Number);
    return { year, month };
}

export function shiftMonth({ year, month }: GoalMonth, delta: number): GoalMonth {
    const index = year * 12 + (month - 1) + delta;
    return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

export function goalProgress(achieved: number, target: number | null | undefined): number | null {
    if (!target || target <= 0) return null;
    return Math.round((achieved / target) * 1000) / 10;
}

/// Meta em branco ou zero não conta: o painel só mostra quem tem alguma.
export function hasGoal(row: SalesGoalRank): boolean {
    return goalProgress(row.achievedRevenue, row.revenueTarget) !== null
        || goalProgress(row.achievedSales, row.salesTarget) !== null;
}

export function progressLevel(percent: number): ProgressLevel {
    if (percent >= 100) return 'done';
    return percent >= 50 ? 'onTrack' : 'behind';
}

export function goalsPayload(month: GoalMonth, rows: SalesGoalInput[]): SaveSalesGoalsRequest {
    return {
        year: month.year,
        month: month.month,
        goals: rows.map((row) => ({
            userId: row.userId,
            revenueTarget: row.revenueTarget || null,
            salesTarget: row.salesTarget || null,
        })),
    };
}

/// Quem está mais perto da meta primeiro: faturamento, depois vendas; sem meta vai para o fim, pelo que vendeu.
export function rankGoals<T extends SalesGoalRank>(rows: T[]): T[] {
    const score = (progress: number | null) => progress ?? -1;

    return [...rows].sort((a, b) =>
        score(goalProgress(b.achievedRevenue, b.revenueTarget)) - score(goalProgress(a.achievedRevenue, a.revenueTarget))
        || score(goalProgress(b.achievedSales, b.salesTarget)) - score(goalProgress(a.achievedSales, a.salesTarget))
        || b.achievedRevenue - a.achievedRevenue
        || a.name.localeCompare(b.name));
}

export interface SalesGoalRank {
    name: string;
    achievedRevenue: number;
    revenueTarget?: number | null;
    achievedSales: number;
    salesTarget?: number | null;
}
